import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  api,
  type CalendarColor,
  type Label as LabelData,
  type ProjectOutcome,
  type Template,
  type TemplateInput,
  type TemplateLibrary,
  type TemplateType,
} from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorAlert } from "@/components/error-alert";
import { LabelChips } from "@/components/label-chips";
import { LinkChips } from "@/components/link-chips";
import { LibraryBoard } from "@/components/library-board";
import { PageSkeleton } from "@/components/page-skeleton";
import { TypeChips } from "@/components/type-chips";
import { cn } from "@/lib/utils";
import { typeLabel } from "@/lib/domain";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

type Draft = {
  type: TemplateType | null;
  title: string;
  description: string;
  duration: string;
  suggestedStart: string;
  deadline: string;
  sortOrder: string;
  isPriority: boolean;
  trackStreak: boolean;
  labelIds: string[];
  links: { key: number; title: string; url: string }[];
};

let linkSeq = 1;
const newLink = (title = "", url = "") => ({ key: linkSeq++, title, url });

const emptyDraft: Draft = {
  type: null,
  title: "",
  description: "",
  duration: "30",
  suggestedStart: "",
  deadline: "",
  sortOrder: "0",
  isPriority: false,
  trackStreak: true,
  labelIds: [],
  links: [],
};

function draftOf(t: Template): Draft {
  return {
    type: t.type,
    title: t.title,
    description: t.description,
    duration: String(t.duration_minutes),
    suggestedStart: t.suggested_start,
    deadline: t.deadline,
    sortOrder: String(t.sort_order),
    isPriority: t.is_priority,
    trackStreak: t.track_streak,
    labelIds: t.label_ids,
    links: t.links.map((l) => newLink(l.title, l.url)),
  };
}

export function TemplatesPage() {
  const [library, setLibrary] = useState<TemplateLibrary | null>(null);
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [error, setError] = useState<string | null>(null);
  // null = editor closed; "new" = creating; otherwise the Template being edited.
  const [editing, setEditing] = useState<"new" | Template | null>(null);

  const load = useCallback(async () => {
    try {
      const [lib, labelData] = await Promise.all([
        api.templates(),
        api.labels(),
      ]);
      setLibrary(lib);
      setLabels(labelData.labels);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Templates"));
    }
  }, []);

  useEffect(() => {
    void load();
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch {
        // Colours are decoration; Labels still list by name.
      }
    })();
  }, [load]);

  if (error && !library) return <ErrorAlert message={error} />;
  if (!library) return <PageSkeleton />;

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Templates</CardTitle>
            <CardDescription>
              Reusable blueprints, grouped by Label. Priority items sit on top,
              then sort order, then title. A Template with several Labels
              appears in each of its columns.
            </CardDescription>
          </div>
          <Button
            onClick={() => setEditing(editing === "new" ? null : "new")}
            disabled={labels.length === 0}
          >
            {editing === "new" ? "Close" : "New Template"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {labels.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Templates need at least one Label. Create your Labels in Settings
              first.
            </p>
          )}
          {editing !== null && (
            <TemplateEditor
              key={editing === "new" ? "new" : editing.id}
              template={editing === "new" ? null : editing}
              labels={labels}
              colors={colors}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
          {library.templates.length === 0 && labels.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              No Templates yet. Create one, or promote an idea from the
              Wishlist.
            </p>
          ) : (
            <LibraryBoard
              library={library}
              labels={labels}
              colors={colors}
              renderCard={(t, _label, hex) => (
                <TemplateCard
                  template={t}
                  accent={hex}
                  active={
                    editing !== null && editing !== "new" && editing.id === t.id
                  }
                  onEdit={() => setEditing(t)}
                />
              )}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TemplateCard({
  template,
  accent,
  active,
  onEdit,
}: {
  template: Template;
  accent?: string;
  active: boolean;
  onEdit: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className={cn(
        "w-full rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:bg-secondary/60",
        active ? "border-primary" : "border-border",
      )}
      style={
        accent ? { borderLeftWidth: 4, borderLeftColor: accent } : undefined
      }
    >
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {template.title}
        </span>
        {template.is_priority && <Badge>Priority</Badge>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <Badge variant="outline">{typeLabel(template.type)}</Badge>
        <span>
          {formatDuration(template.duration_minutes)}
          {template.type === "project" ? " left" : ""}
        </span>
        {template.suggested_start && <span>at {template.suggested_start}</span>}
        {template.deadline && <span>due {template.deadline}</span>}
        {template.type === "repeating" && template.track_streak && (
          <span>Streak {template.streak}/5</span>
        )}
      </div>
      <LinkChips links={template.links} className="mt-1.5" />
    </button>
  );
}

function TemplateEditor({
  template,
  labels,
  colors,
  onClose,
  onSaved,
}: {
  template: Template | null;
  labels: LabelData[];
  colors: CalendarColor[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(
    template ? draftOf(template) : emptyDraft,
  );
  const [busy, setBusy] = useState(false);
  const [askOutcome, setAskOutcome] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const durationHint =
    draft.type === "project"
      ? "Total remaining effort. Each session uses your Project session length."
      : "Length of each scheduled Activity.";

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!draft.type) {
      setError("Pick a Type.");
      return;
    }
    if (draft.labelIds.length === 0) {
      setError("Pick at least one Label.");
      return;
    }
    const body: TemplateInput = {
      type: draft.type,
      title: draft.title.trim(),
      description: draft.description.trim(),
      duration_minutes: Number(draft.duration),
      suggested_start: draft.suggestedStart,
      deadline: draft.type === "project" ? draft.deadline : "",
      sort_order: Number(draft.sortOrder) || 0,
      is_priority: draft.isPriority,
      track_streak: draft.type === "repeating" ? draft.trackStreak : true,
      label_ids: draft.labelIds,
      links: draft.links
        .filter((l) => l.url.trim() !== "")
        .map((l) => ({ title: l.title.trim(), url: l.url.trim() })),
    };
    setBusy(true);
    setError(null);
    try {
      if (template) await api.updateTemplate(template.id, body);
      else await api.createTemplate(body);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not save Template"));
    }
    setBusy(false);
  }

  async function handleDelete(outcome?: ProjectOutcome) {
    if (!template) return;
    if (
      !outcome &&
      !window.confirm(
        `Delete the Template "${template.title}"? Past Activities keep their data.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteTemplate(template.id, outcome);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not delete Template"));
    }
    setBusy(false);
  }

  return (
    <form
      className="rounded-xl border border-border bg-secondary/40 p-4"
      onSubmit={(ev) => void handleSubmit(ev)}
    >
      <p className="text-sm font-medium">
        {template ? `Edit "${template.title}"` : "New Template"}
      </p>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div className="grid gap-1.5 md:col-span-2">
          <Label htmlFor="tpl-title">Title</Label>
          <Input
            id="tpl-title"
            value={draft.title}
            onChange={(e) => set("title", e.target.value)}
            required
          />
        </div>
        <div className="grid gap-1.5 md:col-span-2">
          <span className="text-sm font-medium">Type</span>
          <TypeChips value={draft.type} onChange={(v) => set("type", v)} />
        </div>
        <div className="grid gap-1.5 md:col-span-2">
          <span className="text-sm font-medium">Labels</span>
          <LabelChips
            labels={labels}
            colors={colors}
            picked={draft.labelIds}
            onToggle={(id) =>
              set(
                "labelIds",
                draft.labelIds.includes(id)
                  ? draft.labelIds.filter((l) => l !== id)
                  : [...draft.labelIds, id],
              )
            }
          />
        </div>
        <div className="grid gap-1.5 md:col-span-2">
          <Label htmlFor="tpl-description">Description</Label>
          <Input
            id="tpl-description"
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="tpl-duration">Duration (minutes)</Label>
          <Input
            id="tpl-duration"
            type="number"
            min={1}
            value={draft.duration}
            onChange={(e) => set("duration", e.target.value)}
            required
          />
          <p className="text-xs text-muted-foreground">{durationHint}</p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="tpl-start">Suggested start</Label>
          <Input
            id="tpl-start"
            type="time"
            value={draft.suggestedStart}
            onChange={(e) => set("suggestedStart", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Used when scheduling without picking a slot. Optional.
          </p>
        </div>
        {draft.type === "project" && (
          <div className="grid gap-1.5">
            <Label htmlFor="tpl-deadline">Deadline</Label>
            <Input
              id="tpl-deadline"
              type="date"
              value={draft.deadline}
              onChange={(e) => set("deadline", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Optional. The day after, the project is closed and removed:
              completed if no effort is left, failed otherwise.
            </p>
          </div>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="tpl-sort">Sort order</Label>
          <Input
            id="tpl-sort"
            type="number"
            value={draft.sortOrder}
            onChange={(e) => set("sortOrder", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Lower comes first within a Label column.
          </p>
        </div>
        <div className="flex items-start gap-2 pt-6">
          <input
            id="tpl-priority"
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-primary"
            checked={draft.isPriority}
            onChange={(e) => set("isPriority", e.target.checked)}
          />
          <Label htmlFor="tpl-priority" className="leading-tight">
            Priority
            <span className="block text-xs font-normal text-muted-foreground">
              Sits above the rest of its Label column.
            </span>
          </Label>
        </div>
        {draft.type === "repeating" && (
          <div className="flex items-start gap-2 md:col-span-2">
            <input
              id="tpl-track-streak"
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-primary"
              checked={draft.trackStreak}
              onChange={(e) => set("trackStreak", e.target.checked)}
            />
            <Label htmlFor="tpl-track-streak" className="leading-tight">
              Track streak
              <span className="block text-xs font-normal text-muted-foreground">
                Keep a 0–5 streak that moves when you mark this Done in a
                Planning Session. Turn off for things that recur without being a
                habit worth scoring.
              </span>
            </Label>
          </div>
        )}
        <div className="md:col-span-2">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium">Links</span>
            <span className="text-xs text-muted-foreground">
              Go into the calendar event, so the tool is one tap away.
            </span>
          </div>
          <ul className="mt-2 space-y-2">
            {draft.links.map((l, i) => (
              <li key={l.key} className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label={`Link ${i + 1} title`}
                  className="w-40"
                  value={l.title}
                  onChange={(e) =>
                    set(
                      "links",
                      draft.links.map((x) =>
                        x.key === l.key ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Project plan"
                />
                <Input
                  aria-label={`Link ${i + 1} URL`}
                  className="min-w-56 flex-1"
                  type="url"
                  value={l.url}
                  onChange={(e) =>
                    set(
                      "links",
                      draft.links.map((x) =>
                        x.key === l.key ? { ...x, url: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="https://"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Remove link"
                  onClick={() =>
                    set(
                      "links",
                      draft.links.filter((x) => x.key !== l.key),
                    )
                  }
                >
                  ✕
                </Button>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            disabled={draft.links.length >= 10}
            onClick={() => set("links", [...draft.links, newLink()])}
          >
            Add link
          </Button>
        </div>
      </div>
      {error && (
        <div className="mt-3">
          <ErrorAlert message={error} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {template ? "Save Template" : "Create Template"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </Button>
        {template && (
          <Button
            type="button"
            variant="ghost"
            className="ml-auto text-destructive"
            onClick={() =>
              template.type === "project"
                ? setAskOutcome(true)
                : void handleDelete()
            }
            disabled={busy}
          >
            Delete
          </Button>
        )}
      </div>
      {askOutcome && template && (
        <div className="mt-3 rounded-md border p-3">
          <p className="text-sm">
            Delete "{template.title}". Was the Project completed successfully?
            Either way it is removed; the answer is remembered.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={busy}
              onClick={() => void handleDelete("completed")}
            >
              Completed successfully
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => void handleDelete("not_completed")}
            >
              Not completed
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => setAskOutcome(false)}
            >
              Keep it
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
